import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Checkbox,
  FormControlLabel,
  Typography,
} from '@material-ui/core';
import Zoom from 'react-medium-image-zoom';
import 'react-medium-image-zoom/dist/styles.css';

/**
 * @function OutlierShorelinesDialog
 * @description Dialog listing shoreline dates flagged as outliers, letting the user pick which
 * ones to exclude from the analysis before confirming.
 * @param {Object} props - Component properties.
 * @param {boolean} props.open - Whether the dialog is open.
 * @param {Array<Object>} props.outlierShorelines - Flagged outlier shorelines (timeStart, label, thumbnail).
 * @param {Function} props.onConfirm - Called with the selected timeStarts when confirmed.
 * @param {Function} [props.onToggle] - Called with (outlier, willCheck) when a checkbox is toggled.
 * @param {Function} props.onClose - Called when the dialog is dismissed.
 * @param {Function} props.t - Translation function.
 * @see {@link toggle}
 * **Called by:
 * @see {ShoreRAnalysisSteps}
 * @memberof module:ShoreR
 * @returns {JSX.Element} The outlier shorelines selection dialog.
 */
const OutlierShorelinesDialog = ({ open, outlierShorelines, onConfirm, onToggle, onClose, t }) => {
  const [selectedTimeStarts, setSelectedTimeStarts] = useState([]);

  useEffect(() => {
    if (open) setSelectedTimeStarts([]);
  }, [open]);

  /**
   * @function toggle
   * @description Toggles an outlier shoreline's selection state and notifies the parent.
   * @param {Object} outlier - Outlier shoreline entry, with a `timeStart` id.
   * **Called by:
   * @see {OutlierShorelinesDialog} - via each shoreline checkbox's onChange.
   * @memberof module:ShoreR
   * @returns {void}
   */
  const toggle = (outlier) => {
    const { timeStart } = outlier;
    const willCheck = !selectedTimeStarts.includes(timeStart);
    setSelectedTimeStarts((prev) =>
      prev.includes(timeStart) ? prev.filter((value) => value !== timeStart) : [...prev, timeStart],
    );
    if (onToggle) onToggle(outlier, willCheck);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t('forms.shoreR.outlierDialog.title')}</DialogTitle>
      <DialogContent>
        <Typography style={{ marginBottom: 12, fontSize: 14 }}>
          {t('forms.shoreR.outlierDialog.description')}
        </Typography>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {outlierShorelines.map((outlier) => (
            <FormControlLabel
              key={outlier.timeStart}
              control={
                <Checkbox
                  checked={selectedTimeStarts.includes(outlier.timeStart)}
                  onChange={() => toggle(outlier)}
                  color="primary"
                />
              }
              label={
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {outlier.thumbnail && (
                    <div onClick={(e) => e.stopPropagation()}>
                      <Zoom
                        zoomMargin={50}
                        overlayBgColorStart="rgba(255, 255, 255, 0)"
                        overlayBgColorEnd="rgba(255, 255, 255, 0.5)">
                        <img
                          src={outlier.thumbnail}
                          alt={outlier.label}
                          style={{
                            height: 48,
                            width: 48,
                            objectFit: 'cover',
                            border: '1px solid #ccc',
                            borderRadius: 4,
                            cursor: 'zoom-in',
                          }}
                        />
                      </Zoom>
                    </div>
                  )}
                  <span>{outlier.label}</span>
                </div>
              }
            />
          ))}
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onConfirm(selectedTimeStarts)} color="primary">
          {t('forms.shoreR.outlierDialog.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OutlierShorelinesDialog;
